import React, {useState} from 'react';
import {Visit} from '../../../features/visits/models/visit.model';
import {Calendar, Clock, CheckCircle, AlertTriangle, XCircle, Package, Camera, MessageSquare, Eye, ChevronDown, ChevronUp, TrendingUp} from 'lucide-react';
import VisitDetailsModal from './VisitDetailsModal';
import {formatGregorianDateTime} from '../../utils/dateUtils';

interface VisitListCardProps {
  visits: Visit[];
  loading?: boolean;
  error?: string | null;
}

const VisitListCard: React.FC<VisitListCardProps> = ({visits, loading = false, error = null}) => {
  const [selectedVisit, setSelectedVisit] = useState<Visit | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [expandedVisits, setExpandedVisits] = useState<Set<number>>(new Set());

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return <CheckCircle className="w-4 h-4 text-green-600" />;
      case 'in_progress':
        return <Clock className="w-4 h-4 text-brand-600" />;
      case 'cancelled':
        return <XCircle className="w-4 h-4 text-red-600" />;
      default:
        return <AlertTriangle className="w-4 h-4 text-gray-600" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
        return 'text-green-600 bg-green-50 border-green-200';
      case 'in_progress':
        return 'text-brand-600 bg-brand-50 border-brand-200';
      case 'cancelled':
        return 'text-red-600 bg-red-50 border-red-200';
      default:
        return 'text-gray-600 bg-gray-50 border-gray-200';
    }
  };

  const formatDate = (dateString: string) => {
    return formatGregorianDateTime(dateString);
  };

  const handleVisitClick = (visit: Visit) => {
    setSelectedVisit(visit);
    setIsModalOpen(true);
  };

  const toggleExpanded = (visitId: number) => {
    const newExpanded = new Set(expandedVisits);
    if (newExpanded.has(visitId)) {
      newExpanded.delete(visitId);
    } else {
      newExpanded.add(visitId);
    }
    setExpandedVisits(newExpanded);
  };

  if (loading) {
    return (
      <div className="bg-white border border-gray-200 rounded-xl p-4">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-8 h-8 bg-gray-200 rounded-full animate-pulse"></div>
          <div className="flex-1">
            <div className="h-4 bg-gray-200 rounded animate-pulse mb-2"></div>
            <div className="h-3 bg-gray-200 rounded animate-pulse w-2/3"></div>
          </div>
        </div>
        <div className="space-y-3">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-16 bg-gray-200 rounded animate-pulse"></div>
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white border border-red-200 rounded-xl p-4">
        <div className="flex items-center gap-3 text-red-600">
          <AlertTriangle className="w-5 h-5" />
          <div>
            <div className="font-medium">خطأ في تحميل بيانات الزيارات</div>
            <div className="text-sm opacity-75">{error}</div>
          </div>
        </div>
      </div>
    );
  }

  if (!visits || visits.length === 0) {
    return (
      <div className="bg-white border border-gray-200 rounded-xl p-4">
        <div className="text-center text-gray-500">
          <Calendar className="w-8 h-8 mx-auto mb-2 text-gray-300" />
          <div className="font-medium">لا توجد زيارات مكتملة</div>
          <div className="text-sm">لم يتم تسجيل أي زيارات مكتملة لهذا المتجر بعد</div>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="bg-white border border-gray-200 rounded-xl p-4">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
            <Calendar className="w-4 h-4 text-green-600" />
          </div>
          <div>
            <h3 className="font-semibold text-gray-900">الزيارات المكتملة</h3>
            <div className="text-sm text-gray-600">{visits.length} زيارة مكتملة</div>
          </div>
        </div>

        <div className="space-y-3">
          {visits.map((visit) => {
            const isExpanded = expandedVisits.has(visit.id);
            return (
              <div key={visit.id} className="border border-gray-200 rounded-lg overflow-hidden">
                {/* Visit Header */}
                <div
                  className="p-4 cursor-pointer hover:bg-gray-50 transition-colors"
                  onClick={() => toggleExpanded(visit.id)}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      {getStatusIcon(visit.status)}
                      <div>
                        <div className="font-medium text-gray-900">
                          زيارة #{visit.id}
                        </div>
                        <div className="text-sm text-gray-600">
                          {formatDate(visit.visitDate)} • {visit.visitor.fullName}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium border ${getStatusColor(visit.status)}`}>
                        {visit.statusText}
                      </span>
                      {isExpanded ? (
                        <ChevronUp className="w-4 h-4 text-gray-400" />
                      ) : (
                        <ChevronDown className="w-4 h-4 text-gray-400" />
                      )}
                    </div>
                  </div>
                </div>

                {/* Expanded Content */}
                {isExpanded && (
                  <div className="border-t border-gray-200 p-4 bg-gray-50">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                      {/* Visit Items Count */}
                      {visit.visitItems && visit.visitItems.length > 0 && (
                        <div className="flex items-center gap-2 text-sm">
                          <Package className="w-4 h-4 text-gray-600" />
                          <span className="text-gray-600">{visit.visitItems.length} منتج</span>
                        </div>
                      )}

                      {/* Visit Images Count */}
                      {visit.visitImages && visit.visitImages.length > 0 && (
                        <div className="flex items-center gap-2 text-sm">
                          <Camera className="w-4 h-4 text-gray-600" />
                          <span className="text-gray-600">{visit.visitImages.length} صورة</span>
                        </div>
                      )}

                      {/* Notes Indicator */}
                      {visit.notes && (
                        <div className="flex items-center gap-2 text-sm">
                          <MessageSquare className="w-4 h-4 text-gray-600" />
                          <span className="text-gray-600">ملاحظات</span>
                        </div>
                      )}
                    </div>

                    {/* Quick Preview of Notes */}
                    {visit.notes && (
                      <div className="mb-4 p-3 bg-white rounded-lg border border-gray-200">
                        <div className="text-xs text-gray-500 font-medium mb-1">ملاحظات سريعة</div>
                        <div className="text-sm text-gray-700 line-clamp-2">
                          {visit.notes.length > 100 ? `${visit.notes.substring(0, 100)}...` : visit.notes}
                        </div>
                      </div>
                    )}

                    {/* Visit Activity Summary */}
                    <div className="mb-4 p-3 bg-white rounded-lg border border-gray-200">
                      <div className="text-xs text-gray-500 font-medium mb-2 flex items-center gap-2">
                        <TrendingUp className="w-4 h-4" />
                        ملخص النشاط
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <div className="flex items-center gap-2 text-sm">
                          <Package className="w-4 h-4 text-green-600" />
                          <span className="text-gray-600">{visit.visitItems?.length || 0} منتج محدث</span>
                        </div>
                        <div className="flex items-center gap-2 text-sm">
                          <Camera className="w-4 h-4 text-brand-600" />
                          <span className="text-gray-600">{visit.visitImages?.length || 0} صورة مضافة</span>
                        </div>
                      </div>
                    </div>

                    {/* Visit Images Preview */}
                    {visit.visitImages && visit.visitImages.length > 0 && (
                      <div className="mb-4 p-3 bg-white rounded-lg border border-gray-200">
                        <div className="text-xs text-gray-500 font-medium mb-2 flex items-center gap-2">
                          <Camera className="w-4 h-4" />
                          معاينة الصور ({visit.visitImages.length})
                        </div>
                        <div className="grid grid-cols-3 gap-2">
                          {visit.visitImages.slice(0, 6).map((image) => (
                            <div key={image.id} className="relative group">
                              <img
                                src={image.imageUrl}
                                alt={image.description || `صورة ${image.imageType}`}
                                className="w-full h-16 object-cover rounded border border-gray-200 cursor-pointer hover:shadow-md transition-shadow"
                                onClick={() => handleVisitClick(visit)}
                                title="انقر لعرض الصورة بالحجم الكامل"
                              />
                              <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-20 transition-all rounded flex items-center justify-center">
                                <div className="opacity-0 group-hover:opacity-100 transition-opacity">
                                  <Eye className="w-4 h-4 text-white" />
                                </div>
                              </div>
                              <div className="absolute top-1 right-1">
                                <div className="text-xs bg-brand-100 text-brand-700 px-1 py-0.5 rounded text-[10px]">
                                  {image.imageType}
                                </div>
                              </div>
                            </div>
                          ))}
                          {visit.visitImages.length > 6 && (
                            <div className="w-full h-16 bg-gray-100 rounded border border-gray-200 flex items-center justify-center cursor-pointer hover:bg-gray-200 transition-colors"
                                 onClick={() => handleVisitClick(visit)}>
                              <div className="text-center">
                                <div className="text-xs text-gray-600 font-medium">+{visit.visitImages.length - 6}</div>
                                <div className="text-xs text-gray-500">المزيد</div>
                              </div>
                            </div>
                          )}
                        </div>
                        {visit.visitImages.length > 6 && (
                          <div className="mt-2 text-center">
                            <button
                              onClick={() => handleVisitClick(visit)}
                              className="text-xs text-brand-600 hover:text-brand-800 font-medium"
                            >
                              عرض جميع الصور ({visit.visitImages.length})
                            </button>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Action Buttons */}
                    <div className="flex gap-2">
                      <button
                        onClick={() => handleVisitClick(visit)}
                        className="flex-1 flex items-center justify-center gap-2 px-3 py-2 bg-brand-100 text-brand-600 rounded-lg hover:bg-brand-200 transition-colors text-sm font-medium"
                      >
                        <Eye className="w-4 h-4" />
                        عرض التفاصيل الكاملة
                      </button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Visit Details Modal */}
      <VisitDetailsModal
        visit={selectedVisit}
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setSelectedVisit(null);
        }}
      />
    </>
  );
};

export default VisitListCard;
